import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@tellescope/ui";
import type { IframeBlock as IframeBlockType, LeafBlock } from "../../../shared/schema";
import { useBlockVisibility } from "../../hooks/useBlockVisibility";
import { TextBlock } from "./TextBlock";
import { HeadingBlock } from "./HeadingBlock";
import { ButtonBlock } from "./ButtonBlock";
import { CardBlock } from "./CardBlock";
import { SpacerBlock } from "./SpacerBlock";
import { DividerBlock } from "./DividerBlock";
import { BannerBlock } from "./BannerBlock";
import { HtmlBlock } from "./HtmlBlock";
import { RichTextBlock } from "./RichTextBlock";
import { PopupBlock } from "./PopupBlock";
import { AppointmentListBlock } from "./AppointmentListBlock/AppointmentListBlock";
import { OrderListBlock } from "./OrderListBlock";
import { ChatRoomListBlock } from "./ChatRoomListBlock";
import { FileListBlock } from "./FileListBlock";
import { FormResponseListBlock } from "./FormResponseListBlock";
import { PinnedFormsListBlock } from "./PinnedFormsListBlock";
import { ProfileEditBlock } from "./ProfileEditBlock";
import { ContentListBlock } from "./ContentListBlock";
import { PurchaseListBlock } from "./PurchaseListBlock";
import { FileUploadBlock } from "./FileUploadBlock";
import { StripeSubscriptionsBlock } from "./StripeSubscriptionsBlock";
import { StripeBillingPortalButtonBlock } from "./StripeBillingPortalButtonBlock/StripeBillingPortalButtonBlock";
import { StripeInvoicesBlock } from "./StripeInvoicesBlock/StripeInvoicesBlock";
import { AppointmentBookingBlock } from "./AppointmentBookingBlock";
import { VitalDataBlock } from "./VitalDataBlock/VitalDataBlock";
import { WeightMacroTrackerBlock } from "./WeightMacroTrackerBlock/WeightMacroTrackerBlock";
import { useIframeBlock } from "./useIframeBlock";

interface IframeBlockProps {
  block: IframeBlockType;
}

const FallbackChildBlock: React.FC<{ block: LeafBlock }> = ({ block }) => {
  switch (block.type) {
    case "text":
      return <TextBlock content={block.props.content} variant={block.props.variant} />;

    case "heading":
      return <HeadingBlock text={block.props.text} level={block.props.level} />;

    case "button":
      return <ButtonBlock text={block.props.text} variant={block.props.variant} href={block.props.href} />;

    case "card":
      return <CardBlock title={block.props.title} description={block.props.description} />;

    case "spacer":
      return <SpacerBlock size={block.props.size} />;

    case "divider":
      return <DividerBlock />;

    case "banner":
      return (
        <BannerBlock
          content={block.props.content}
          variant={block.props.variant}
          dismissible={block.props.dismissible}
        />
      );

    case "html":
      return <HtmlBlock content={block.props.content} />;

    case "rich-text":
      return <RichTextBlock content={block.props.content} />;

    case "popup":
      return <PopupBlock block={block} />;

    case "appointment-list":
      return <AppointmentListBlock block={block} />;

    case "order-list":
      return <OrderListBlock block={block} />;

    case "chatroom-list":
      return <ChatRoomListBlock block={block} />;

    case "file-list":
      return <FileListBlock block={block} />;

    case "form-response-list":
      return <FormResponseListBlock block={block} />;

    case "pinned-forms-list":
      return <PinnedFormsListBlock block={block} />;

    case "profile-edit":
      return <ProfileEditBlock block={block} />;

    case "content-list":
      return <ContentListBlock block={block} />;

    case "purchase-list":
      return <PurchaseListBlock block={block} />;

    case "file-upload":
      return <FileUploadBlock block={block} />;

    case "stripe-subscriptions":
      return <StripeSubscriptionsBlock block={block} />;

    case "stripe-billing-portal-button":
      return <StripeBillingPortalButtonBlock block={block} />;

    case "stripe-invoices":
      return <StripeInvoicesBlock block={block} />;

    case "appointment-booking":
      return <AppointmentBookingBlock block={block} />;

    case "vital-data":
      return <VitalDataBlock block={block} />;

    case "weight-macro-tracker":
      return <WeightMacroTrackerBlock block={block} />;

    default: {
      const _exhaustive: never = block;
      return null;
    }
  }
};

/**
 * Wraps a fallback block with visibility evaluation using the full async context.
 * Returns null if the block's visibility condition evaluates to false.
 */
const VisibleFallbackChildBlock: React.FC<{ block: LeafBlock }> = ({ block }) => {
  const { isVisible } = useBlockVisibility(block);

  if (!isVisible) {
    return null;
  }

  return <FallbackChildBlock block={block} />;
};

export const IframeBlock: React.FC<IframeBlockProps> = ({ block }) => {
  const { t } = useTranslation();
  const { finalUrl, sandboxAttr, heightStyle, isLoading, showFallback, fallbackBlocks, title } = useIframeBlock(block);

  if (showFallback && fallbackBlocks.length > 0) {
    return (
      <div className="flex flex-col gap-4">
        {fallbackBlocks.map((childBlock) => (
          <VisibleFallbackChildBlock key={childBlock.id} block={childBlock} />
        ))}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-muted/30 rounded-lg" style={{ height: heightStyle }}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!finalUrl) {
    return null;
  }

  return (
    <iframe
      src={finalUrl}
      title={title ?? t("Embedded content")}
      sandbox={sandboxAttr}
      className={cn("w-full border-0 rounded-lg")}
      style={{ height: heightStyle }}
    />
  );
};
